import { FC, useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import { basePrefixApi, EnvironmentWithBucket } from '../services/api';

interface BasePrefixSelectorProps {
  onPrefixChange?: (prefix: string) => void;
}

interface BasePrefixData {
  currentEnvironmentId: string;
  environments: EnvironmentWithBucket[];
}

export const BasePrefixSelector: FC<BasePrefixSelectorProps> = ({ onPrefixChange }) => {
  const [data, setData] = useState<BasePrefixData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    // Small delay to ensure Keycloak is fully initialized
    const timer = setTimeout(() => {
      loadBasePrefixes();
    }, 150);
    return () => clearTimeout(timer);
  }, []);

  // Save to localStorage when environment changes
  useEffect(() => {
    if (data?.currentEnvironmentId) {
      localStorage.setItem('s3-content-manager-environment-id', data.currentEnvironmentId);
    }
  }, [data?.currentEnvironmentId]);

  const loadBasePrefixes = async () => {
    setLoading(true);
    setError(null);
    try {
      const environments = await basePrefixApi.getEnvironmentsWithBuckets();
      if (environments.length === 0) {
        setError('No environments found');
        return;
      }

      // Try to restore saved environment, otherwise use first one
      const savedEnvironmentId = localStorage.getItem('s3-content-manager-environment-id');
      const currentEnv = savedEnvironmentId && environments.find(e => e.environmentId === savedEnvironmentId)
        ? savedEnvironmentId
        : environments[0].environmentId;

      setData({ currentEnvironmentId: currentEnv, environments });

      // Set the environment on the backend to match the frontend state
      try {
        await basePrefixApi.setEnvironmentById(currentEnv);
        const env = environments.find(e => e.environmentId === currentEnv);
        console.log(`✅ Environment synchronized to backend: ${env?.environmentName} (${env?.prefix})`);

        // Notify parent of the current environment prefix
        if (onPrefixChange && env) {
          onPrefixChange(env.prefix);
        }
      } catch (err) {
        console.warn('Failed to set initial environment on backend:', err);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load environments';
      // If it's a 401, it might be a timing issue - retry once
      if (err && typeof err === 'object' && 'response' in err && (err as any).response?.status === 401) {
        console.warn('Auth error loading environments, retrying...');
        setTimeout(() => loadBasePrefixes(), 500);
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEnvironmentChange = async (newEnvironmentId: string) => {
    if (!data || newEnvironmentId === data.currentEnvironmentId) return;

    setChanging(true);
    setError(null);
    try {
      await basePrefixApi.setEnvironmentById(newEnvironmentId);
      setData({ ...data, currentEnvironmentId: newEnvironmentId });

      // Notify parent of the new environment's prefix
      const env = data.environments.find(e => e.environmentId === newEnvironmentId);
      if (onPrefixChange && env) {
        onPrefixChange(env.prefix);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change environment');
    } finally {
      setChanging(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Loading environments...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  }

  if (!data || data.environments.length === 0) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        No environments found. Make sure your S3 bucket has the proper folder structure.
      </Alert>
    );
  }

  const currentEnv = data.environments.find(e => e.environmentId === data.currentEnvironmentId);

  return (
    <>
      <Box sx={{ minWidth: 200, display: 'flex', alignItems: 'center', gap: 1 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Environment</InputLabel>
          <Select
            value={data.currentEnvironmentId}
            label="Environment"
            onChange={(e) => handleEnvironmentChange(e.target.value)}
            disabled={changing}
            renderValue={(value) => {
              const env = data.environments.find(e => e.environmentId === value);
              return (
                <Box>
                  <Typography variant="body2" component="span">{env?.environmentName || env?.prefix}</Typography>
                  {env && (
                    <Typography variant="caption" component="span" sx={{ ml: 1, opacity: 0.7 }}>
                      ({env.bucketName})
                    </Typography>
                  )}
                </Box>
              );
            }}
          >
            {data.environments.map((env) => (
              <MenuItem key={env.environmentId} value={env.environmentId}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Box>
                    <Typography variant="body2">{env.environmentName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {env.prefix} - {env.bucketName}
                    </Typography>
                  </Box>
                  {env.environmentId === data.currentEnvironmentId && (
                    <Chip label="Active" size="small" color="primary" variant="outlined" />
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {changing && (
          <Typography variant="caption" color="text.secondary">
            Switching...
          </Typography>
        )}
      </Box>
    </>
  );
};